import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateTime } from 'luxon';
import { DateRangeValidationConfig } from '../filters.model';

export function FilterDateRangeValidator(
  startDateKey: string, 
  endDateKey: string, 
  config?: DateRangeValidationConfig
): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const startDate = formGroup.get(startDateKey)?.value;
    const endDate = formGroup.get(endDateKey)?.value;

    // If either date is not set, don't validate
    if (!startDate || !endDate) {
      return null;
    }

    // Convert to DateTime objects
    const start = startDate instanceof DateTime ? startDate : DateTime.fromISO(startDate);
    const end = endDate instanceof DateTime ? endDate : DateTime.fromISO(endDate);

    // Basic validation: start date must be before end date
    if (start > end) {
      return {
        dateRangeError: {
          message: 'Start date must be before end date',
          startDate,
          endDate
        }
      };
    }

    // Min date validation
    if (config?.minDate && start < config.minDate) {
      return {
        minDateError: {
          message: `Start date cannot be before ${config.minDate.toFormat('yyyy-MM-dd')}`,
          minDate: config.minDate,
          startDate
        }
      };
    }

    // Max date validation
    if (config?.maxDate && end > config.maxDate) {
      return {
        maxDateError: {
          message: `End date cannot be after ${config.maxDate.toFormat('yyyy-MM-dd')}`,
          maxDate: config.maxDate,
          endDate
        }
      };
    }

    // Future dates validation
    if (config?.allowFutureDates === false && (end > DateTime.now() || start > DateTime.now())) {
      return {
        futureDateError: {
          message: 'End date cannot be in the future',
          endDate
        }
      };
    }

    // Range duration validation
    const rangeDays = end.diff(start, 'days').days;
    
    if (config?.maxRangeDays && rangeDays > config.maxRangeDays) {
      return {
        maxRangeError: {
          message: `Date range cannot exceed ${config.maxRangeDays} days`,
          maxRangeDays: config.maxRangeDays,
          actualRangeDays: rangeDays
        }
      };
    }

    if (config?.minRangeDays && rangeDays < config.minRangeDays) {
      return {
        minRangeError: {
          message: `Date range must be at least ${config.minRangeDays} days`,
          minRangeDays: config.minRangeDays,
          actualRangeDays: rangeDays
        }
      };
    }

    return null;
  };
} 